import _ from 'lodash';
import { getFormattedFloatValues, calculatePercentage } from '../../../utils/number-utils';
import { getFormattedTime, getFormattedDate, getFormattedSeconds } from '../../../utils/date-utils';

export function getAverageTimeSpentData(analyticsData) {
    const totalAverageTimeForVisitors = getTotalAverageTimeForVisitors(analyticsData);
    const averageOfAverageTimeForVisitors = getAverageOfAverageTimeForVisitors(analyticsData);
    const analyticsForAverageTimeData = {};

    analyticsData.forEach((pageData) => {
        const key = pageData.shortUrlFormat.toLowerCase();
        const analyticsTimeItem = analyticsForAverageTimeData[key];

        const averageTimeForVisitors = pageData.isLatest ? pageData.averageTimeForActiveVisitors : pageData.averageTimeForExpiredVisitors;

        if (!analyticsTimeItem) {
            const averageTimePercentage = calculatePercentage(averageTimeForVisitors, totalAverageTimeForVisitors);
            const formattedAverageTimeForVisitors = getFormattedSeconds(averageTimeForVisitors);
            analyticsForAverageTimeData[key] = {
                accessedUrl: pageData.accessedURL,
                shortUrlFormat: pageData.shortUrlFormat,
                averageTimeForVisitors,
                averageTimePercentage,
                formattedAverageTimeForVisitors
            };
        } else {
            analyticsForAverageTimeData[key].averageTimeForVisitors += averageTimeForVisitors;
            analyticsForAverageTimeData[key].averageTimePercentage = calculatePercentage(analyticsForAverageTimeData[key].averageTimeForVisitors, totalAverageTimeForVisitors);
            analyticsForAverageTimeData[key].formattedAverageTimeForVisitors = getFormattedSeconds(analyticsForAverageTimeData[key].averageTimeForVisitors);
        }
    });

    const formattedAnalyticsData = getArrayFromDictionary(analyticsForAverageTimeData).sort((a, b) => {
        return b.averageTimeForVisitors - a.averageTimeForVisitors;
    });

    return {
        totalAverageTimeForVisitors,
        averageOfAverageTimeForVisitors,
        analyticsData: formattedAnalyticsData,
    };
}

export function getAnalyticsOverTimeData(analyticsData) {
    const analyticsOverTimeData = {};
    const siteDataKeys = [];

    analyticsData.forEach((item) => {
        const key = `${item.formattedCreatedDate}_${item.formattedCreatedTime}`;
        const analyticsTimeItem = analyticsOverTimeData[key];

        const siteDataKey = `${item.formattedCreatedDate}_${item.formattedCreatedTime}_${item.accessedURL}`;
        const category = { categoryName: item.categoryName, numberOfAcceptedCookies: item.numberOfAcceptedCookies };

        if (!analyticsTimeItem) {
            analyticsOverTimeData[key] = {
                activeVisitors: item.numberOfActiveVisitors,
                categories: [category],
                dateText: item.formattedCreatedDate,
                timeText: item.formattedCreatedTime,
            };
            siteDataKeys.push(siteDataKey);
        } else {
            let activeVisitors = analyticsTimeItem.activeVisitors

            if (!siteDataKeys.includes(siteDataKey)) {
                activeVisitors = analyticsTimeItem.activeVisitors + item.numberOfActiveVisitors;
                siteDataKeys.push(siteDataKey);
            }
            let categoryData = analyticsTimeItem.categories ? analyticsTimeItem.categories : [];

            if (!categoryData.some(e => e.categoryName === category.categoryName)) {
                categoryData.push(category);
            }

            analyticsOverTimeData[key] = {
                ...analyticsTimeItem,
                activeVisitors,
                categories: categoryData
            };
        }
    });
    return analyticsOverTimeData;
}

export function getAnalyticsOverPageData(analyticsData) {
    const analyticsForPagesData = {};
    const totalActiveVisitors = getTotalAmountOfActiveVisitors(analyticsData);
    const siteDataKeys = [];
    analyticsData.forEach((pageData) => {
        const key = `${pageData.shortUrlFormat.toLowerCase()}`;
        const averageTimeForVisitors = pageData.isLatest ? pageData.averageTimeForActiveVisitors : pageData.averageTimeForExpiredVisitors;
        const analyticsForPageItem = analyticsForPagesData[key];

        const siteDataKey = `${pageData.formattedCreatedDate}_${pageData.formattedCreatedTime}_${pageData.accessedURL}`;

        var numberOfAcceptedAllCookies = pageData.numberOfAcceptedAllCookies;
        if (!analyticsForPageItem) {
            analyticsForPagesData[key] = {
                shortUrlFormat: pageData.shortUrlFormat,
                averageTimeForActiveVisitors: averageTimeForVisitors,
                formattedAverageTimeForActiveVisitors: getFormattedSeconds(averageTimeForVisitors),
                activeVisitors: pageData.numberOfActiveVisitors,
                visitorsThatAcceptedCookies: numberOfAcceptedAllCookies,
                visitorsThatRejectedCookies: pageData.numberOfActiveVisitors - numberOfAcceptedAllCookies,
            };

            siteDataKeys.push(siteDataKey);
        } else {
            const averageTimeForActiveVisitors = analyticsForPageItem.averageTimeForActiveVisitors + averageTimeForVisitors;

            let activeVisitors = analyticsForPageItem.activeVisitors;
            let visitorsThatAcceptedCookies = analyticsForPageItem.visitorsThatAcceptedCookies;

            if (!siteDataKeys.includes(siteDataKey)) {
                activeVisitors = analyticsForPageItem.activeVisitors + pageData.numberOfActiveVisitors;
                visitorsThatAcceptedCookies = analyticsForPageItem.visitorsThatAcceptedCookies + pageData.numberOfAcceptedAllCookies;
                siteDataKeys.push(siteDataKey);
            }

            analyticsForPagesData[key] = {
                ...analyticsForPageItem,
                averageTimeForActiveVisitors,
                formattedAverageTimeForActiveVisitors: getFormattedSeconds(averageTimeForActiveVisitors),
                activeVisitors,
                visitorsThatAcceptedCookies,
                visitorsThatRejectedCookies: activeVisitors - visitorsThatAcceptedCookies
            };
        }
    });

    Object.keys(analyticsForPagesData).forEach((key) => {
        const analyticsForPagesDataItem = analyticsForPagesData[key];
        analyticsForPagesData[key].visitedPercentage = calculatePercentage(analyticsForPagesDataItem.activeVisitors, totalActiveVisitors);
    });

    return getArrayFromDictionary(analyticsForPagesData).sort((a, b) => {
        return b.visitedPercentage - a.visitedPercentage;
    });
}

export function getAnalyticsPagesRadarData(analyticsData) {
    const radarCaptions = {};
    const radarData = {};
    const visitorsData = {};
    let totalNumberOfActiveVisitors = 0;
    const siteDataKeys = [];
    analyticsData.forEach((pageData) => {
        const key = getKeyByAccessedUrl(pageData.accessedURL);

        const siteDataKey = `${pageData.formattedCreatedDate}_${pageData.formattedCreatedTime}_${pageData.accessedURL}`;
        if (!siteDataKeys.includes(siteDataKey)) {
            totalNumberOfActiveVisitors += pageData.numberOfActiveVisitors;
            siteDataKeys.push(siteDataKey);

            if (!visitorsData[key]) {
                radarCaptions[key] = key;
                visitorsData[key] = pageData.numberOfActiveVisitors;
            } else {
                visitorsData[key] += pageData.numberOfActiveVisitors;
            }
        }
    });

    Object.keys(visitorsData).forEach((key) => {
        radarData[key] = calculatePercentage(visitorsData[key], totalNumberOfActiveVisitors) / 100;
    });

    return { radarCaptions, radarData };
}

function getKeyByAccessedUrl(accessedUrl) {
    const splitAccessedUrl = accessedUrl.split('/');
    const visitedPage = splitAccessedUrl[3] === undefined ||
        splitAccessedUrl[3] === '' ?
        '/' :
        splitAccessedUrl[3];
    return !visitedPage ? '/' : visitedPage.toLowerCase();
}

export function getTotalAmountOFVisitorsThatAcceptedCookies(analyticsData) {
    let allKeys = [];

    const visitorsAcceptedAllCookies = (accumulator, currentValue) => {

        var key = `${currentValue.createdOnUTC}_${currentValue.accessedURL}`;
        if (!allKeys.includes(key)) {
            allKeys.push(key);
            return accumulator + currentValue.numberOfAcceptedAllCookies;
        }
        return accumulator;
    }

    return analyticsData.reduce(visitorsAcceptedAllCookies, 0);
}

function getTotalAverageTimeForVisitors(analyticsData) {
    const averageTimeForVisitorsReducer = (accumulator, currentValue) => {
        if (currentValue.isLatest) {
            return accumulator + currentValue.averageTimeForActiveVisitors;
        }
        return accumulator + currentValue.averageTimeForExpiredVisitors;
    };
    const totalAverageTimeForVisitors = analyticsData.reduce(averageTimeForVisitorsReducer, 0);
    return getFormattedFloatValues(totalAverageTimeForVisitors);
}

function getAverageOfAverageTimeForVisitors(analyticsData) {
    const averageTimeForVisitorsReducer = (accumulator, currentValue) => {
        if (currentValue.isLatest) {
            return accumulator + currentValue.averageTimeForActiveVisitors;
        }
        return accumulator + currentValue.averageTimeForExpiredVisitors;
    };
    const totalAverageTimeForVisitors = analyticsData.reduce(averageTimeForVisitorsReducer, 0);
    return getFormattedFloatValues(totalAverageTimeForVisitors / analyticsData.length);
}

export function getTotalAmountOfActiveVisitors(analyticsData) {
    let allKeys = [];

    const activeVisitorsReducer = (accumulator, currentValue) => {

        var key = `${currentValue.createdOnUTC}_${currentValue.accessedURL}`;
        if (!allKeys.includes(key)) {
            allKeys.push(key);
            return accumulator + currentValue.numberOfActiveVisitors;
        }
        return accumulator;
    }

    return analyticsData.reduce(activeVisitorsReducer, 0);
}

export function getAllAvailableDistinctDates(analyticsData) {
    const cloneOfAnalyticsData = _.cloneDeep(analyticsData);
    const differentDates = [...new Set(cloneOfAnalyticsData.map((data) => data.created.setHours(0, 0, 0, 0)))];
    return differentDates.map((date) => {
        return {
            id: _.uniqueId('date_'),
            value: new Date(date),
            text: getFormattedDate(new Date(date))
        };
    });
}

export function getAllAvailableTimes(analyticsData) {
    const availableTimes = {};
    analyticsData.forEach((data) => {
        const currentDate = new Date(data.created);
        const formattedDate = getFormattedDate(currentDate);

        if (!availableTimes[formattedDate]) {
            availableTimes[formattedDate] = [];
        }

        availableTimes[formattedDate].push({
            id: _.uniqueId('time_'),
            value: currentDate,
            timeText: getFormattedTime(currentDate),
            dateText: getFormattedDate(currentDate),
            activeVisitors: data.numberOfActiveVisitors,
            accessedURL: data.accessedURL
        });
    });

    return availableTimes;
}

function getArrayFromDictionary(dictionaryToConvert) {
    return Object.keys(dictionaryToConvert).map((key) => {
        return dictionaryToConvert[key];
    });
}
